import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ArrowLeft, Package, Factory, ShoppingCart, Truck, RotateCcw } from 'lucide-react';

export default function BatchInfo() {
  const { batchNo } = useParams();
  const navigate = useNavigate();
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBatchInfo();
  }, [batchNo]);

  const fetchBatchInfo = async () => {
    try {
      const response = await api.get(`/batch-info/${batchNo}`);
      setBatchData(response.data);
    } catch (error) {
      toast.error('Failed to load batch information');
      if (error.response?.status === 404) {
        navigate('/inventory');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!batchData) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-500">Batch not found</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  const { purchase, productions, finished_goods, byproducts, linked_orders, linked_returns, summary } = batchData;

  return (
    <div className="space-y-6" data-testid="batch-info-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">
            Batch: {batchNo}
          </h1>
          <div className="flex gap-2 mt-1">
            {summary.factory && <Badge variant="outline">{summary.factory}</Badge>}
            <Badge className={purchase ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
              {purchase ? 'Purchase Linked' : 'No Purchase Record'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-700">Raw Maize</p>
            <p className="text-2xl font-bold text-blue-800">{formatNumber(summary.raw_maize_kg)} kg</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50">
          <CardContent className="pt-6">
            <p className="text-sm text-emerald-700">Current Stock</p>
            <p className="text-2xl font-bold text-emerald-800">{formatNumber(summary.current_stock_bales)} bales</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-700">Dispatched</p>
            <p className="text-2xl font-bold text-amber-800">{formatNumber(summary.dispatched_bales)} bales</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50">
          <CardContent className="pt-6">
            <p className="text-sm text-purple-700">Byproducts</p>
            <p className="text-2xl font-bold text-purple-800">{formatNumber(summary.current_byproducts_kg)} kg</p>
          </CardContent>
        </Card>
      </div>

      {/* Purchase Info */}
      {purchase && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              Purchase Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Date</p>
                <p className="font-medium">{purchase.date}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Supplier</p>
                <p className="font-medium">{purchase.supplier_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Factory</p>
                <p className="font-medium">{purchase.factory || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Kg Received</p>
                <p className="font-medium">{formatNumber(purchase.kg_received)} kg</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Cost</p>
                <p className="font-medium">{formatCurrency(purchase.total_cost)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Paid Amount</p>
                <p className="font-medium text-green-600">{formatCurrency(purchase.paid_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Balance</p>
                <p className={`font-medium ${purchase.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(purchase.balance)}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <Badge className={purchase.batch_status === 'Processed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                  {purchase.batch_status || 'Raw'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Production Records */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Factory className="h-5 w-5 text-emerald-600" />
            Production Records ({productions?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {productions?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Factory</TableHead>
                  <TableHead className="text-right">Flour (kg)</TableHead>
                  <TableHead>Byproducts</TableHead>
                  <TableHead className="text-right">Total Byproducts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productions.map((prod, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{prod.date}</TableCell>
                    <TableCell>
                      {prod.factory ? <Badge variant="outline">{prod.factory}</Badge> : '-'}
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(prod.maize_flour_produced_kg)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {prod.byproducts?.map((bp, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {bp.name}: {formatNumber(bp.quantity_kg)}kg
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatNumber(prod.total_byproducts_kg)} kg</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-slate-500 text-center py-4">No production records for this batch</p>
          )}
        </CardContent>
      </Card>

      {/* Current Inventory */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Finished Goods */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Finished Goods Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            {finished_goods?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pack Size</TableHead>
                    <TableHead className="text-right">Bales</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {finished_goods.map((fg, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{fg.pack_size_kg}kg</TableCell>
                      <TableCell className="text-right font-medium">{formatNumber(fg.bales)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-slate-500 text-center py-4">No finished goods in this batch</p>
            )}
          </CardContent>
        </Card>

        {/* Byproducts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Byproduct Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            {byproducts?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Quantity (kg)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byproducts.map((bp, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{bp.name}</TableCell>
                      <TableCell className="text-right font-medium">{formatNumber(bp.quantity_kg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-slate-500 text-center py-4">No byproducts in this batch</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Linked Orders */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-600" />
            Linked Dispatched Orders ({linked_orders?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {linked_orders?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linked_orders.map((order, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{order.order_number}</TableCell>
                    <TableCell>{order.date}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>
                      <Badge className={
                        order.dispatch_status === 'Dispatched' ? 'bg-green-100 text-green-700' :
                        order.dispatch_status === 'Partially Dispatched' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }>
                        {order.dispatch_status || 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(order.order_total)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/orders/${order.id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-slate-500 text-center py-4">No orders linked to this batch</p>
          )}
        </CardContent>
      </Card>

      {/* Linked Returns */}
      {linked_returns?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-red-600" />
              Linked Returns ({linked_returns.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linked_returns.map((ret, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{ret.date}</TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => navigate(`/orders/${ret.order_id}`)}
                      >
                        View Order
                      </Button>
                    </TableCell>
                    <TableCell>{ret.items?.length || 0} items</TableCell>
                    <TableCell className="text-slate-500">{ret.remarks || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
